import { useEffect, useState } from "react";
import { AnimatePresence, m } from "framer-motion";
import { MessageCircle, Phone, Sparkles, X } from "lucide-react";
import { BRAND, IMAGES, waLink } from "@/lib/site";

export default function OfferPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("ov_offer_seen")) return;
    let shown = false;
    const show = () => {
      if (shown) return;
      shown = true;
      setOpen(true);
      sessionStorage.setItem("ov_offer_seen", "1");
    };
    const timer = setTimeout(show, 6000);
    const onScroll = () => {
      const max = document.body.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max > 0.45) show();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <m.div
          data-testid="offer-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] grid place-items-center bg-plum-950/70 p-5 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Special offer on massages"
        >
          <m.div
            initial={{ opacity: 0, y: 40, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-sm overflow-hidden rounded-3xl border border-magenta/30 bg-plum-900 shadow-[0_30px_90px_rgba(217,38,148,0.35)]"
          >
            <button
              data-testid="offer-close-button"
              onClick={() => setOpen(false)}
              aria-label="Close offer"
              className="absolute right-3 top-3 z-10 grid h-9 w-9 place-items-center rounded-full bg-plum-950/60 text-cream/80 backdrop-blur transition-colors hover:bg-plum-950 hover:text-cream"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="relative h-40">
              <img
                src={IMAGES.aroma}
                alt="Relaxation massage at Orchid Vue Spa"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-plum-900 to-transparent" />
              <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-magenta px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-cream">
                <Sparkles className="h-3.5 w-3.5" />
                Special Offer
              </span>
            </div>
            <div className="p-6 pt-4">
              <h3 className="font-serif text-2xl font-medium leading-tight text-cream">
                Enjoy our{" "}
                <span className="italic text-magenta">Relaxation Massage</span>
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-lavender">
                Special offer on all massages today — fully sanitized private
                rooms, 8th floor Orchid Vue Hotel, Bur Dubai. Limited slots
                daily.
              </p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <a
                  data-testid="offer-whatsapp-button"
                  href={waLink(
                    "Hello Orchid Vue Spa! I saw the Special Offer on your website — I'd like to book a Relaxation Massage."
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 rounded-full bg-wa py-3.5 text-sm font-bold text-plum-950 transition-transform duration-300 hover:scale-[1.03]"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </a>
                <a
                  data-testid="offer-call-button"
                  href={BRAND.callLink}
                  className="flex items-center justify-center gap-2 rounded-full border border-champagne/40 py-3.5 text-sm font-semibold text-champagne transition-colors duration-300 hover:bg-champagne hover:text-plum-950"
                >
                  <Phone className="h-4 w-4" />
                  Call Now
                </a>
              </div>
            </div>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
}
